import { useQuery, useQueryClient, useMutation } from "react-query";
import { useDispatch } from "react-redux";

import { getErrorMessage } from "helpers";
import { uiActions } from "store";
import {
	DocumentMediaMutation,
	DocumentsMutationOptions,
	ErrorResponse,
	SortBy,
} from "types";

import { insertDocument } from "./documentsCacheHelpers";
import { DocumentsServiceContextType } from "./documentsServiceContext";

export type UseAttachmentServiceOptions = DocumentsServiceContextType;

export type UseMutationParams<TSuccess = any, TError = ErrorResponse> = {
	onSuccessCb?: (result?: TSuccess) => any;
	onErrorCb?: (error?: TError) => void;
};

const useDocumentsService = ({ key, service }: UseAttachmentServiceOptions) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const handleShowError = (error: ErrorResponse) => {
		dispatch(
			uiActions.showErrorNotification({
				title: getErrorMessage(error),
			}),
		);
	};

	const useDocumentsListQuery = ({
		entityId,
		page,
		sortBy,
	}: {
		entityId: string | number;
		page: number;
		sortBy: SortBy | undefined;
	}) => {
		const fetchDocuments = () => {
			return service.getDocuments(entityId, { page }, sortBy);
		};

		const queryResult = useQuery(
			[key, entityId, page, sortBy],
			fetchDocuments,
			{
				keepPreviousData: true,
				enabled: !!entityId,
			},
		);

		return { ...queryResult };
	};

	const useAddDocumentMutation = (
		page: number,
		sortBy: SortBy | undefined,
		{ onErrorCb }: UseMutationParams = {},
	) => {
		return useMutation(
			({ entityId, document }: DocumentsMutationOptions) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.addDocument(entityId, document);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess(newDocument, { entityId }) {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Document successfully added",
						}),
					);

					queryClient.setQueryData<any>(
						[key, entityId, page, sortBy],
						(oldData: any) => {
							if (oldData?.items) {
								return {
									...oldData,
									count: oldData?.count + 1,
									items: insertDocument(
										oldData?.items,
										newDocument,
										!sortBy?.desc,
									),
								};
							} else {
								return insertDocument(oldData, newDocument);
							}
						},
					);
				},
				onError(error: ErrorResponse) {
					handleShowError(error);
					onErrorCb?.(error);
				},
			},
		);
	};

	const useViewDocumentMutation = () => {
		return useMutation(
			({ entityId, documentId }: DocumentMediaMutation) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.viewDocument(entityId, documentId);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
			},
		);
	};

	const useDownloadDocumentMutation = () => {
		return useMutation(
			({ entityId, documentId }: DocumentMediaMutation) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.downloadDocument(entityId, documentId);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
			},
		);
	};

	const useDeleteDocumentMutation = (refetch: Function) => {
		return useMutation(
			({ entityId, documentId }: DocumentMediaMutation) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.deleteDocument(entityId, documentId);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess() {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Document successfully deleted",
						}),
					);
					refetch();
				},
				onError(error: ErrorResponse) {
					handleShowError(error);
				},
			},
		);
	};

	return {
		useDocumentsListQuery,
		useAddDocumentMutation,
		useDeleteDocumentMutation,
		useViewDocumentMutation,
		useDownloadDocumentMutation,
	};
};

export default useDocumentsService;
